<template>
  <div class="business-registry-search">
    <AkInput
      v-model="searchQuery"
      :label="$t('retailer.germanBusinessIdentifier.registry.label')"
      :placeholder="$t('retailer.germanBusinessIdentifier.registry.placeholder')"
      :disabled="disabled"
      :errors="errors"
      :has-error="errors.length > 0"
      size="medium"
      data-testid="registry-number-input"
      @blur="emit('blur')"
    />

    <div
      v-if="isLoading"
      class="business-registry-search__loading"
      data-testid="search-loading"
    >
      <AkSkeleton
        width="100%"
        height="48px"
      />
      <AkSkeleton
        width="100%"
        height="48px"
      />
    </div>

    <div
      v-else-if="searchResults.length > 0 && !selectedResult"
      class="business-registry-search__results"
      data-testid="search-results"
    >
      <div
        v-for="result in searchResults"
        :key="result.id"
        class="business-registry-search__result"
        data-testid="search-result-item"
        @click="selectResult(result)"
      >
        <AkParagraph
          weight="bold"
          size="sm"
        >
          {{ result.companyName }}
        </AkParagraph>
        <AkParagraph
          size="xs"
          class="business-registry-search__result-details"
        >
          {{ result.registryNumber }} - {{ result.registryCourt }}
        </AkParagraph>
      </div>
    </div>

    <div
      v-else-if="showNoResults"
      class="business-registry-search__no-results"
      data-testid="no-results"
    >
      <AkParagraph size="sm">
        {{ $t('retailer.germanBusinessIdentifier.registry.noResults') }}
        <button
          class="business-registry-search__use-it-anyway"
          data-testid="use-it-anyway"
          @click="handleUseItAnyway"
        >
          {{ $t('retailer.germanBusinessIdentifier.registry.useItAnyway') }}
        </button>
      </AkParagraph>
    </div>

    <div
      v-if="selectedResult && !disabled"
      class="business-registry-search__selected"
      data-testid="selected-result"
    >
      <div class="business-registry-search__selected-info">
        <AkParagraph weight="bold">
          {{ selectedResult.companyName }}
        </AkParagraph>
        <AkParagraph
          size="sm"
          class="business-registry-search__selected-details"
        >
          {{ selectedResult.registryNumber }} · {{ selectedResult.registryCourt }}
        </AkParagraph>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { AkInput, AkParagraph, AkSkeleton } from '@ankorstore/design-system';
import { searchRegistry } from '../services/german-bi.service';
import { validDeTaxNumber, trackSearchPerformed, trackResultSelected, trackNoResults } from '../composables/use-german-bi';
import type { RegistrySearchResult } from '../types/german-bi.types';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

const props = withDefaults(
  defineProps<{
    modelValue: string;
    disabled?: boolean;
    errors?: unknown[];
  }>(),
  {
    disabled: false,
    errors: () => [],
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  select: [result: RegistrySearchResult];
  'selection-change': [hasSelection: boolean];
  'use-it-anyway': [];
  'no-results': [];
  'input-change': [];
  blur: [];
}>();

const searchQuery = ref(props.modelValue || '');
const searchResults = ref<RegistrySearchResult[]>([]);
const selectedResult = ref<RegistrySearchResult | null>(null);
const isLoading = ref(false);
const hasSearched = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const showNoResults = computed(
  () =>
    searchQuery.value.length >= MIN_QUERY_LENGTH &&
    validDeTaxNumber(searchQuery.value) &&
    searchResults.value.length === 0 &&
    !isLoading.value &&
    hasSearched.value &&
    !selectedResult.value
);

async function autoSearchExistingValue(): Promise<void> {
  if (!props.modelValue || props.disabled || props.modelValue.length < MIN_QUERY_LENGTH) {
    return;
  }

  isLoading.value = true;
  try {
    const results = await searchRegistry(props.modelValue);
    if (results.length > 0) {
      selectedResult.value = results[0];
      emit('select', results[0]);
      emit('selection-change', true);
    } else {
      emit('no-results');
    }
  } finally {
    isLoading.value = false;
    hasSearched.value = true;
  }
}

onMounted(autoSearchExistingValue);

watch(
  () => props.disabled,
  (newDisabled, oldDisabled) => {
    if (oldDisabled && !newDisabled && props.modelValue && !selectedResult.value) {
      autoSearchExistingValue();
    }
  }
);

watch(searchQuery, (query) => {
  hasSearched.value = false;

  if (selectedResult.value) {
    selectedResult.value = null;
    emit('selection-change', false);
  }

  emit('update:modelValue', query);
  emit('input-change');

  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (props.disabled || query.length < MIN_QUERY_LENGTH) {
    searchResults.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    if (!validDeTaxNumber(query)) {
      searchResults.value = [];
      hasSearched.value = true;
      return;
    }

    isLoading.value = true;
    try {
      searchResults.value = await searchRegistry(query);
      trackSearchPerformed(query);
      if (searchResults.value.length === 0) {
        trackNoResults(query);
      }
    } finally {
      isLoading.value = false;
      hasSearched.value = true;
    }
  }, DEBOUNCE_MS);
});

function handleUseItAnyway(): void {
  emit('use-it-anyway');
}

function selectResult(result: RegistrySearchResult): void {
  selectedResult.value = result;
  searchResults.value = [];
  emit('update:modelValue', searchQuery.value);
  emit('select', result);
  emit('selection-change', true);
  trackResultSelected(result.companyName, result.registryNumber);
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue && selectedResult.value) {
      selectedResult.value = null;
      searchQuery.value = '';
    } else if (newValue && newValue !== searchQuery.value) {
      searchQuery.value = newValue;
    }
  }
);
</script>

<style scoped lang="scss">
.business-registry-search {
  @apply ds-w-full ds-relative;

  &__loading {
    @apply ds-flex ds-flex-col ds-gap-2 ds-mt-2;
  }

  &__results {
    @apply ds-absolute ds-left-0 ds-right-0 ds-mt-1 ds-bg-white ds-border ds-border-neutral-300 ds-rounded-md ds-shadow-md ds-z-10 ds-overflow-y-auto ds-max-h-[240px];
  }

  &__result {
    @apply ds-p-3 ds-cursor-pointer ds-border-b ds-border-neutral-300;

    &:last-child {
      @apply ds-border-b-0;
    }

    &:hover {
      @apply ds-bg-neutral-100;
    }

    &-details {
      @apply ds-text-neutral-600 ds-mt-1;
    }
  }

  &__no-results {
    @apply ds-mt-2 ds-text-primary;
  }

  &__use-it-anyway {
    @apply ds-underline ds-bg-transparent ds-border-none ds-cursor-pointer ds-p-0 ds-text-primary;
  }

  &__selected {
    @apply ds-flex ds-items-start ds-justify-between ds-mt-3 ds-p-4 ds-bg-neutral-100 ds-border ds-border-neutral-300 ds-rounded-md ds-gap-3;

    &-info {
      @apply ds-flex-1 ds-flex ds-flex-col ds-gap-1;
    }

    &-details {
      @apply ds-text-neutral-700;
    }
  }
}
</style>
